import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSofteamboardStore } from '../../store/softeamboardStore';
// useWebSocket is handled at store level to avoid duplicate subscriptions
import api, { jobConnectApi, needsApi, uploadsApi, workflowRequestsApi } from '../../services/api';
import { X, FileText, XCircle, Clock, Star, Download, Edit3, Sparkles, Brain, Plus, ExternalLink, User, Briefcase, CheckCircle, Target, TrendingUp, Award, Building, Users, MapPinIcon, GraduationCap, Upload, RefreshCw } from 'lucide-react';
import Badge from '../ui/Badge';
import Toast from '../ui/Toast';
import AddCandidateModal from './AddCandidateModal';
import CandidateCard from './CandidateCard';
import CandidateDetailModal from './CandidateDetailModal';
import FileUploadButton from '../common/FileUploadButton';
import { useNeedAnalysisPolling } from '../../hooks/useAnalysisPolling';
import EditNeedModal from './EditNeedModal';
import EmailGenerationModal from './EmailGenerationModal';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import { useNavigate } from 'react-router-dom';
import EditCandidateModal from './EditCandidateModal';
import { debounce } from 'lodash';
import JobConnectDiffusionPanel from '../../features/jobconnect/JobConnectDiffusionPanel';
import JobConnectApplicationsInbox from '../../features/jobconnect/JobConnectApplicationsInbox';

const ProcessTracker = ({ needId, isOpen, onClose, onNeedUpdate, initialWorkspace = 'need' }) => {
  const {
    needs,
    moveNeed, // ✅ ADD: For column/status updates
    getCandidatesWithStatusForNeed,
    loadCandidatesWithStatusForNeed,
    // ✅ ADD: Use store's step functions
    getStepsForCandidate,
    loadStepsForCandidate,
    // ✅ ADD: Use store's scoring data functions
    getScoringDataForNeed,
    updateCandidateScoringData,
    // ✅ PERFORMANCE: Removed candidateSteps subscription - was causing unnecessary re-renders
    // Use getStepsForCandidate() instead which accesses cached data without global subscription
    // ✅ NEW: Get candidatesById for direct dependency tracking
    candidatesById,
    user
  } = useSofteamboardStore();
  const isRecruiter = user?.role === 'Recruiter';
  const canManageJobConnect = process.env.REACT_APP_JOBCONNECT_ENABLED === 'true'
    && (user?.role === 'Admin' || user?.role === 'Sales');
  const [activeWorkspace, setActiveWorkspace] = useState(initialWorkspace);
  const [activeTab, setActiveTab] = useState('need');
  const [receivedApplicationsCount, setReceivedApplicationsCount] = useState(0);

  const needStatusLabel = {
    'no-candidate': 'À qualifier',
    'candidate-identified': 'Candidats identifiés',
    'candidate-sent': 'Candidats envoyés',
    'candidate-interview': 'Entretiens',
    'go-nogo': 'Décision en cours',
    sold: 'Gagné',
    canceled: 'Annulé',
    deleted: 'Clôturé',
    frozen: 'En pause',
    push: 'Prioritaire',
  };
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  
  // Toast state
  const [toast, setToast] = useState(null);

  // New state for real candidate data
  const [candidates, setCandidates] = useState([]);
  const [candidateDetails, setCandidateDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needData, setNeedData] = useState(null);

  // Step management loading states
  const [stepLoading, setStepLoading] = useState({
    sale: false,
    canceled: false,
    delete: false,
    freeze: false,
    push: false  // Add push loading state
  });

  // Add state for edit modal
  const [showEditNeedModal, setShowEditNeedModal] = useState(false);

  // Add these state variables after the existing state declarations
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Add tooltip state
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  // ✅ ADD: State for candidate detail modal
  const [showCandidateDetailModal, setShowCandidateDetailModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // ✅ NEW: State for edit candidate modal (for post-AI analysis)
  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false);
  const [candidateToEdit, setCandidateToEdit] = useState(null);

  // Add navigation
  const navigate = useNavigate();

  // Remember previous workflow column to toggle back from FROZEN/PUSH
  const prevColumnRef = useRef({ frozen: null, push: null });

  // JD re-analysis state
  const [analyzingJD, setAnalyzingJD] = useState(false);
  const jdFileInputRef = useRef(null);

  const latestStoreRef = useRef({});
  const activeNeedIdRef = useRef(needId);
  activeNeedIdRef.current = needId;
  latestStoreRef.current = {
    candidatesById,
    getCandidatesWithStatusForNeed,
    getScoringDataForNeed,
  };


  // Always call hooks at the top level
  const { triggerBatchWorkflow, isLoading } = useWorkflowTrigger(needs.find(n => n.id === needId) || needData, onNeedUpdate);

  // ✅ NEW: Determine missing integrations and required data
  const missingOpportunity = !needData?.opportunitySalesforceUrl;
  const missingMission = !needData?.missionSalesforceUrl;
  const missingOrderClients = !needData?.orderClients || needData.orderClients.length === 0;
  const missingTjm = !needData?.tjm || needData.tjm <= 0;
  const showRpaActions = missingOpportunity || missingMission;

  // ✅ FIX: Add helper function to get the most advanced step in workflow
  const getMostAdvancedStepForCandidate = useCallback((candidate) => {
    // Get steps from store
    const steps = getStepsForCandidate(needId, candidate.id);

    if (steps.length === 0) {
      return { type: 'NO_CANDIDATE', date: new Date().toISOString() };
    }


    // ✅ FIX: Define workflow progression order (least advanced first, most advanced last)
    const workflowProgression = [
      'NO_CANDIDATE',
      'CANDIDATE_IDENTIFIED',
      'CANDIDATE_SENT',
      'CANDIDATE_INTERVIEW',
      'GO_NOGO',
      'SALE',
      'CANCELED',
      'DELETED'
    ];

    // ✅ FIX: Find the most advanced step in the workflow
    let mostAdvancedStep = null;
    let highestIndex = -1;

    steps.forEach(step => {
      const stepIndex = workflowProgression.indexOf(step.type);
      if (stepIndex > highestIndex) {
        highestIndex = stepIndex;
        mostAdvancedStep = step;
      }
    });

    const result = mostAdvancedStep || { type: 'NO_CANDIDATE', date: new Date().toISOString() };


    return result;
  }, [getStepsForCandidate, needId]);


  // Derive candidates groups (identified, sent, in‑progress, rejected) with useMemo
  const { identifiedCandidates, sentCandidates, inProgressCandidates, rejectedCandidates } = useMemo(() => {
    const groups = {
      identified: [],
      sent: [],
      in_progress: [],
      rejected: []
    };

    candidates.forEach(candidate => {
      // ✅ FIX: Get the most advanced step instead of just the latest by date
      const mostAdvancedStep = getMostAdvancedStepForCandidate(candidate);
      const stepType = mostAdvancedStep?.type || candidate.status || 'identified';

      // ✅ PERFORMANCE: Removed console.log from candidate categorization loop

      // ✅ FIX: Map step types to candidate groups based on workflow progression
      switch (stepType) {
        case 'SALE':
          // SALE is the final step, but we don't have a "sold" group, so put in in_progress
          groups.in_progress.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
          break;
        case 'CANCELED':
        case 'DELETED':
          // These are final states, put in rejected
          groups.rejected.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
          break;
        case 'GO_NOGO':
          // Check if it's a rejection (NO_GO) or acceptance (GO)
          if (mostAdvancedStep.detailGoNoGo?.decision === 'NO_GO') {
            groups.rejected.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
          } else {
            groups.in_progress.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
          }
          break;
        case 'CANDIDATE_INTERVIEW':
          groups.in_progress.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
          break;
        case 'CANDIDATE_SENT':
          groups.sent.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
          break;
        case 'CANDIDATE_IDENTIFIED':
        case 'NO_CANDIDATE':
        default:
          groups.identified.push({ ...candidate, latestStep: mostAdvancedStep, stepType });
      }
    });

    return {
      identifiedCandidates: groups.identified,
      sentCandidates: groups.sent,
      inProgressCandidates: groups.in_progress,
      rejectedCandidates: groups.rejected
    };
  }, [candidates, getMostAdvancedStepForCandidate]);

  // ✅ ADD: Handler for opening candidate detail modal
  const handleCandidateClick = useCallback((candidate) => {
    setSelectedCandidate(candidate);
    setShowCandidateDetailModal(true);
  }, []);

  // ✅ ADD: Handler for downloading CV
  const handleDownloadCV = useCallback(async (candidate) => {
    try {
      if (candidate.cvUrl) {
        // Use presigned URL endpoint which returns original filename
        const response = await uploadsApi.downloadCv(candidate.id);
        const downloadUrl = response.downloadUrl;
        const filename = response.originalFilename || `CV_${(candidate.name || 'candidate').replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_')}.pdf`;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setToast({
          message: 'No CV available for this candidate',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Failed to download CV:', error);
      setToast({
        message: 'Failed to download CV: ' + error.message,
        type: 'error'
      });
    }
  }, []);

  // ✅ PHASE 3: Stable callback for closing candidate modal
  const handleCloseCandidateModal = useCallback(() => {
    setShowCandidateDetailModal(false);
    setSelectedCandidate(null);
  }, []);

  // ✅ PHASE 3: Stable callback for editing candidate
  const handleEditCandidate = useCallback((candidate) => {
    setCandidateToEdit(candidate);
    setShowEditCandidateModal(true);
    setShowCandidateDetailModal(false);
    setSelectedCandidate(null);
  }, []);

  // ✅ PHASE 3: Stable callback for downloading CV from modal
  const handleDownloadCandidateCV = useCallback(() => {
    if (selectedCandidate) {
      handleDownloadCV(selectedCandidate);
    }
  }, [selectedCandidate, handleDownloadCV]);

  // ✅ FIXED: Compact candidate card component using store function
  const CompactCandidateCard = useCallback(({ candidate }) => {
    const latestStep = getMostAdvancedStepForCandidate(candidate);
    const rejectionReason = latestStep?.detailGoNoGo?.reason || latestStep?.comment || 'No reason provided';
    const rejectionDate = latestStep?.date ? new Date(latestStep.date).toLocaleDateString() : 'Unknown date';
    
    return (
      <button
        type="button"
        className="flex w-full items-center p-3 text-left bg-red-50 border border-red-200 rounded-lg opacity-75 hover:opacity-100 transition-all cursor-pointer hover:shadow-md hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500"
        onClick={() => handleCandidateClick(candidate)}
        title="Consulter le détail du candidat"
      >
        <div className="flex-shrink-0 mr-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-red-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900 truncate">
              {candidate.name}
            </p>
            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
              Rejected
            </span>
          </div>
          <p className="text-xs text-gray-600 truncate mt-1">
            {rejectionReason}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {rejectionDate}
          </p>
        </div>
        <div className="flex-shrink-0 ml-2">
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </div>
      </button>
    );
  }, [getMostAdvancedStepForCandidate, handleCandidateClick]);

  // Memoize callbacks to prevent re-renders - MOVE TO TOP LEVEL
  const handleCandidateUpdate = useCallback((update) => {
    if (update.action === 'DETACHED') {
      console.log('🔌 ProcessTracker - Candidate detached via UI, removing from list:', update.candidateId);
      setCandidates(prev => prev.filter(c => c.id !== update.candidateId));
      
      // Also remove from candidate details
      setCandidateDetails(prev => {
        const newDetails = { ...prev };
        delete newDetails[update.candidateId];
        return newDetails;
      });
    } else if (update.action === 'REATTACH') {
      console.log('🔌 ProcessTracker - Reattaching candidate due to error:', update.candidateId);
      setCandidates(prev => {
        const existing = prev.find(c => c.id === update.candidateId);
        if (!existing && update.candidate) {
          return [...prev, update.candidate];
        }
        return prev;
      });
    }
  }, []);

  const handleAnalyzeCV = useCallback((candidate) => {
    console.log('Analyzing CV for:', candidate.name);
  }, []);

  const handleRateCandidate = useCallback((candidate) => {
    console.log('Rating candidate:', candidate.name);
  }, []);

  // Memoize tooltip handlers
  const showTooltipMemo = useCallback((content, event) => {
    const rect = event.target.getBoundingClientRect();
    setTooltip({
      show: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  }, []);

  const hideTooltipMemo = useCallback(() => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  }, []);

  // Replace the existing showTooltip and hideTooltip functions with:
  const showTooltip = showTooltipMemo;
  const hideTooltip = hideTooltipMemo;

  // ✅ FIX: Add state to track if we've already fetched candidates for this need
  const [hasInitializedCandidates, setHasInitializedCandidates] = useState(false);
  const [lastFetchAttempt, setLastFetchAttempt] = useState(0);

  // ✅ FIX: Add a delay mechanism to wait for store updates
  const [storeUpdatePending, setStoreUpdatePending] = useState(false);

  // ✅ FIX: Simplified WebSocket handler with single refresh mechanism
  const handleWebSocketMessage = useCallback((message) => {
    console.log('🔌 ProcessTracker - WebSocket message received:', message);
    
    try {
      switch (message.event_type) {
        case 'candidate_updated':
        case 'candidate_created':
          // Store global WebSocket handler already calls refreshCandidatesForNeed
          break;
          
        case 'need_updated':
          if (message.entity_id === needId) {
            console.log('🔌 ProcessTracker - Need updated:', message.payload);
            
            // Handle candidate detachment
            if (message.payload?.action === 'CANDIDATE_DETACHED') {
              const detachedCandidateId = message.payload.candidate_id;
              console.log('🔌 ProcessTracker - Candidate detached, removing from local state:', detachedCandidateId);
              
              setCandidates(prev => prev.filter(c => c.id !== detachedCandidateId));
              setCandidateDetails(prev => {
                const newDetails = { ...prev };
                delete newDetails[detachedCandidateId];
                return newDetails;
              });
              return;
            } else {
              // ✅ FIX: Only update need data, don't trigger candidate refresh
              setNeedData(prev => ({
                ...prev,
                ...message.payload,
                column: message.payload.column || prev.column,
                progressPct: message.payload.progressPct ?? message.payload.progress ?? prev.progressPct
              }));
              onNeedUpdate?.();
            }
          }
          break;
          
        case 'scoring_analysis_complete':
          // ✅ FIX: Only update scoring data, don't trigger full refresh
          const scoringData = message.payload || {};
          const candidateId = scoringData.candidate_id;
          const needIdFromMessage = scoringData.need_id;
          
          if (needIdFromMessage === needId && candidateId) {
            console.log('🔌 ProcessTracker - Scoring analysis complete for candidate:', candidateId);
            
            let justification = scoringData.justification;
            if (justification && typeof justification === 'object') {
              justification = JSON.stringify(justification, null, 2);
            }
            
            // ✅ FIX: Update store with scoring data
            updateCandidateScoringData(needId, candidateId, {
              score: scoringData.score,
              justification,
              scoreJobId: message.entity_id,
              scoredAt: message.timestamp
            });
            
            // ✅ FIX: Update local state for immediate UI update
            setCandidates(prev => 
              prev.map(c => c.id === candidateId ? {
                ...c,
                relevanceScore: scoringData.score,
                relevance_score: scoringData.score,
                scoreJustification: justification,
                score_justification: justification,
                scoreJobId: message.entity_id,
                score_job_id: message.entity_id,
                scoredAt: message.timestamp,
                scored_at: message.timestamp
              } : c)
            );
            
            setCandidateDetails(prev => ({
              ...prev,
              [candidateId]: {
                ...prev[candidateId],
                relevanceScore: scoringData.score,
                relevance_score: scoringData.score,
                scoreJustification: justification,
                score_justification: justification,
                scoreJobId: message.entity_id,
                score_job_id: message.entity_id,
                scoredAt: message.timestamp,
                scored_at: message.timestamp
              }
            }));
          }
          break;
          
        // ✅ FIX: Remove step event handlers to prevent multiple refreshes
        // Step updates are handled by candidate_updated events
        
        default:
          // Ignore unknown message types
          break;
      }
    } catch (error) {
      console.error('🔌 ProcessTracker - Error handling WebSocket message:', error);
    }
  }, [needId, onNeedUpdate]);

  // ✅ NEW: Store-only fetchCandidateDetails with no API calls
  const fetchCandidateDetails = useCallback((candidatesData) => {
    if (!candidatesData || candidatesData.length === 0) return;
    
    console.log('🔄 ProcessTracker - Processing candidate details from store for:', candidatesData.length, 'candidates');
    
    try {
      const details = {};
      
      // ✅ ONLY use cached candidate data - no API calls
      for (const candidate of candidatesData) {
        // Use the candidate data we already have from cache, enriched with candidatesById data
        const candidateDetail = { 
          ...candidate,
          // ✅ Ensure we have the most complete data from candidatesById cache
          ...latestStoreRef.current.candidatesById[candidate.id] || {}
        };
        
        details[candidate.id] = candidateDetail;

        // ✅ PERFORMANCE: Removed console.log from candidate processing loop
      }

      setCandidateDetails(details);
      
    } catch (error) {
      console.error('❌ ProcessTracker - Error processing candidate details:', error);
      setError(error.message);
    }
  }, []); // ✅ FIX: Remove candidatesById dependency to prevent infinite loop

  // ✅ NEW: Store-only fetchCandidates with no direct API calls
  const fetchCandidates = useCallback(async (loadedCandidates) => {
    if (!needId) return;
    if (activeNeedIdRef.current !== needId) return;
    
    console.log('🔄 ProcessTracker - Fetching candidates from store for need:', needId);
    
    try {
      setLoading(true);
      
      // ✅ ONLY use store cache - no API calls
      const candidatesData = loadedCandidates || latestStoreRef.current.getCandidatesWithStatusForNeed(needId);
      
      if (!candidatesData || candidatesData.length === 0) {
        console.log('📦 ProcessTracker - No cached candidates available for need:', needId);
        setCandidates([]);
        setHasInitializedCandidates(true);
        return;
      }
      
      console.log('📦 ProcessTracker - Using cached candidates:', candidatesData.length);
      
      // ✅ ADD: Load scoring data from store and merge with candidates
      const storeScoringData = latestStoreRef.current.getScoringDataForNeed(needId);
      const scoringDataMap = {};
      
      if (storeScoringData && storeScoringData.length > 0) {
        storeScoringData.forEach(scoring => {
          scoringDataMap[scoring.candidateId] = scoring;
        });
        console.log('🔌 ProcessTracker - Loaded scoring data from store:', {
          needId,
          scoringCount: storeScoringData.length,
          candidates: storeScoringData.map(s => s.candidateId)
        });
      }
      
      // ✅ ADD: Merge scoring data with candidates
      const candidatesWithScoring = candidatesData.map(candidate => {
        const storeScoring = scoringDataMap[candidate.id];
        if (storeScoring) {
          return {
            ...candidate,
            relevanceScore: storeScoring.score,
            relevance_score: storeScoring.score,
            scoreJustification: storeScoring.justification,
            score_justification: storeScoring.justification,
            scoreJobId: storeScoring.scoreJobId,
            score_job_id: storeScoring.scoreJobId,
            scoredAt: storeScoring.scoredAt,
            scored_at: storeScoring.scoredAt
          };
        }
        return candidate;
      });
      
      setCandidates(candidatesWithScoring);
      setHasInitializedCandidates(true);
      
      // ✅ FIX: Load steps for all candidates
      if (candidatesData.length > 0) {
        const stepPromises = candidatesData.map(async (candidate) => {
          try {
            await loadStepsForCandidate(needId, candidate.id);
          } catch (err) {
            console.error(`Failed to load steps for candidate ${candidate.id}:`, err);
          }
        });
        
        await Promise.all(stepPromises);
      }

      if (activeNeedIdRef.current !== needId) return;
      
      // ✅ ADD: Fetch candidate details
      await fetchCandidateDetails(candidatesWithScoring);
      
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.message);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [needId]); // ✅ FIX: Remove store function dependencies to prevent infinite loop

  // ✅ FIX: Simplified debounced fetch
  const debouncedFetchCandidates = useCallback(
    debounce(async () => {
      await fetchCandidates();
    }, 200), // Reduced debounce time
    [fetchCandidates]
  );

  // ✅ FIX: Update the global function to use debounced version
  useEffect(() => {
    const handleFetchCandidates = () => {
      if (needId && isOpen) {
        debouncedFetchCandidates();
      }
    };

    // Store the function reference to avoid dependency issues
    window.handleFetchCandidates = handleFetchCandidates;
    
    return () => {
      delete window.handleFetchCandidates;
    };
  }, [needId, isOpen, debouncedFetchCandidates]);

  // ✅ FIX: Simplified WebSocket handler without duplicate calls
  const handleWebSocketMessageWithFetch = useCallback((message) => {
    handleWebSocketMessage(message);
    // Remove duplicate fetchCandidates calls
  }, [handleWebSocketMessage]);

  // WebSocket subscription handled globally in store

  // ✅ NEW: Store-only polling with no direct API calls
  const { isPolling } = useNeedAnalysisPolling(
    needId,
    // ✅ FIX: Only poll if there's actual pending analysis work
    isOpen && (
      needData?.jd_analysis_status === 'PENDING' || 
      (candidates.length > 0 && candidates.some(c => candidateDetails[c.id]?.cv_analysis_status === 'PENDING'))
    ),
    // ✅ NEW: Remove API calls from polling - only use store data
    async (nId) => {
      // ✅ ONLY use store data - no API calls
      const storeNeed = needs.find(n => n.id === nId);
      const storeCandidates = getCandidatesWithStatusForNeed(nId);
      
      return { 
        need: storeNeed, 
        candidates: storeCandidates 
      };
    },
    (updatedData) => {
      if (updatedData?.need) {
        setNeedData(updatedData.need);
      }
      if (updatedData?.candidates && updatedData.candidates.length > 0) {
        setCandidates(updatedData.candidates);
        // ✅ Refresh candidate details from store only
        fetchCandidateDetails(updatedData.candidates);
      }
    }
  );

  // ✅ NEW: Store refresh mechanism - ensure data is available when ProcessTracker opens
  useEffect(() => {
    if (isOpen && needId) {
      // ✅ PERFORMANCE: Removed console.log from modal open handler

      const refreshIfNeeded = async () => {
        setLoading(true); // ✅ Show loading state immediately

        try {
          const cachedCandidates = getCandidatesWithStatusForNeed(needId);

          // Only trigger store refresh if truly empty (store handles the API call)
          if (!cachedCandidates || cachedCandidates.length === 0) {
            console.log('📦 ProcessTracker - Store cache empty, triggering store refresh...');
            // ✅ FIX: Wait for data to load before proceeding
            const loadedCandidates = await loadCandidatesWithStatusForNeed(needId);
            await fetchCandidates(loadedCandidates);
            return;
          }
          await fetchCandidates();
        } catch (error) {
          console.error('Error loading candidates:', error);
          setLoading(false);
        }
      };

      refreshIfNeeded();
    }
  }, [isOpen, needId]); // ✅ FIX: Remove store function dependencies to prevent infinite loop

  // ✅ FIX: Enhanced store sync with better conditions and memoization
  const storeNeed = useMemo(() => needs.find(n => n.id === needId), [needs, needId]);

  useEffect(() => {
    if (!isOpen || !needId || storeNeed) return undefined;

    let current = true;
    needsApi.getNeed(needId)
      .then(need => {
        if (current) setNeedData(need);
      })
      .catch(fetchError => {
        if (current) setError(fetchError.message || 'Failed to load need');
      });

    return () => { current = false; };
  }, [isOpen, needId, storeNeed]);

  useEffect(() => {
    // Sync store's need data with local needData when store updates
    if (storeNeed) {
      setNeedData(prev => {
        // Only update if there are actual changes to avoid unnecessary re-renders
        const merged = { ...prev, ...storeNeed };
        if (JSON.stringify(prev) !== JSON.stringify(merged)) {
          // ✅ PERFORMANCE: Removed console.log from sync effect
          return merged;
        }
        return prev;
      });
    }
  }, [storeNeed]);

  // ✅ NEW: Sync candidates from store to local state when store updates
  const storeCandidates = useMemo(() => {
    if (!needId || !isOpen || !hasInitializedCandidates) return null;
    return getCandidatesWithStatusForNeed(needId);
  }, [needId, isOpen, hasInitializedCandidates, getCandidatesWithStatusForNeed]);

  useEffect(() => {
    // Sync store's candidate data with local state when store updates
    // Only sync if we've initialized and the store has data
    if (storeCandidates && storeCandidates.length >= 0 && hasInitializedCandidates) {
      // ✅ PERFORMANCE: Removed console.log from hot sync effect

      // Merge with scoring data
      const scoringDataMap = {};
      const storeScoringData = getScoringDataForNeed(needId);
      if (storeScoringData && storeScoringData.length > 0) {
        storeScoringData.forEach(scoring => {
          scoringDataMap[scoring.candidateId] = scoring;
        });
      }

      const candidatesWithScoring = storeCandidates.map(candidate => {
        const storeScoring = scoringDataMap[candidate.id];
        if (storeScoring) {
          return {
            ...candidate,
            relevanceScore: storeScoring.score,
            relevance_score: storeScoring.score,
            scoreJustification: storeScoring.justification,
            score_justification: storeScoring.justification,
            scoreJobId: storeScoring.scoreJobId,
            score_job_id: storeScoring.scoreJobId,
            scoredAt: storeScoring.scoredAt,
            scored_at: storeScoring.scoredAt
          };
        }
        return candidate;
      });

      setCandidates(candidatesWithScoring);
      
      // Update candidate details for new candidates
      fetchCandidateDetails(candidatesWithScoring);
    }
  }, [storeCandidates, needId, hasInitializedCandidates, getScoringDataForNeed, fetchCandidateDetails]); // ✅ FIX: Remove candidatesById dependency to prevent infinite loop

  // Detect JD analysis completion
  const currentNeed = needData || needs.find(n => n.id === needId);
  const terminalNeed = ['SALE', 'CANCELED', 'DELETED', 'FROZEN', 'sold', 'canceled', 'deleted', 'frozen']
    .includes(currentNeed?.column || currentNeed?.status);
  useEffect(() => {
    if (!isOpen || !canManageJobConnect || !needId) return undefined;
    let current = true;
    jobConnectApi.listApplicationReceipts(needId).then(response => {
      if (!current) return;
      const pending = (response.items || []).filter(item => item.reviewDecision === 'PENDING').length;
      setReceivedApplicationsCount(pending);
    }).catch(() => {
      if (current) setReceivedApplicationsCount(0);
    });
    return () => { current = false; };
  }, [canManageJobConnect, isOpen, needId]);
  useEffect(() => {
    if (!analyzingJD) return;
    const status = needData?.jd_analysis_status || currentNeed?.jdAnalysisStatus;
    if (status && status !== 'PENDING') {
      setAnalyzingJD(false);
      setToast({ message: 'JD analysis completed successfully', type: 'success' });
    }
  }, [analyzingJD, needData?.jd_analysis_status, currentNeed?.jdAnalysisStatus]);

  // And change the need selection logic:
  const need = currentNeed;
  if (!need) {
    if (!isOpen) return null;
    const loadingTitle = initialWorkspace === 'diffusion' ? 'Chargement de la diffusion' : 'Chargement du besoin';
    const loadingMessage = initialWorkspace === 'diffusion' ? 'Ouverture de la diffusion…' : 'Ouverture du besoin…';
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
        <div role="dialog" aria-modal="true" aria-labelledby="process-tracker-loading-title" className="relative w-full max-w-md rounded-xl bg-white px-6 py-10 text-center shadow-xl">
          <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-3 top-3 flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-600">
            <X className="h-5 w-5" />
          </button>
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
          <h2 id="process-tracker-loading-title" className="mt-4 text-lg font-semibold text-gray-900">{loadingTitle}</h2>
          <p className="mt-2 text-sm text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // Handle JD upload success
  const handleJDUpload = (updatedNeed) => {
    // Update the need data with the new JD information
    setNeedData(prev => ({
      ...prev,
      ...updatedNeed,
      jdFile: updatedNeed.jdFile,
      jdContent: updatedNeed.jdContent,
      jdAnalysis: updatedNeed.jdAnalysis,
      jdAnalysisDate: updatedNeed.jdAnalysisDate,
      jdAnalysisStatus: updatedNeed.jdAnalysisStatus,
      aiAnalysis: updatedNeed.aiAnalysis
    }));
    
    // Also update the parent component
    if (onNeedUpdate) {
      onNeedUpdate();
    }
    
    // Show success toast
    setToast({
      message: 'Job description uploaded and analyzed successfully!',
      type: 'success'
    });
  };

  // Handle JD delete success
  const handleJDDelete = () => {
    // Remove JD data from the need
    setNeedData(prev => ({
      ...prev,
      jdFile: null,
      jdContent: null,
      jdAnalysis: null,
      jdAnalysisDate: null,
      jdAnalysisStatus: null
    }));
    
    // Show success toast
    setToast({
      message: 'Job description deleted successfully!',
      type: 'success'
    });
  };

  // Step management functions
  const handleMarkAsSold = async () => {
    try {
      setStepLoading(prev => ({ ...prev, sale: true }));
      
      console.log('🎯 handleMarkAsSold called for needId:', needId); // ✅ ADDED: Error logging
      
      // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
      const updatedNeed = await moveNeed(needId, 'SALE');
      
      console.log('🎯 handleMarkAsSold success:', { 
        needId, 
        updatedStatus: updatedNeed.status,
        updatedColumn: updatedNeed.column 
      });
      
      setNeedData(updatedNeed);
      
      setToast({
        message: 'Need marked as SOLD successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('🎯 handleMarkAsSold error:', { 
        needId, 
        error: error.message,
        errorDetails: error.response?.data 
      }); // ✅ ENHANCED: Better error logging
      
      setToast({
        message: 'Failed to mark need as sold: ' + error.message,
        type: 'error'
      });
    } finally {
      setStepLoading(prev => ({ ...prev, sale: false }));
    }
  };

  const handleMarkAsCanceled = async () => {
    try {
      setStepLoading(prev => ({ ...prev, canceled: true }));
      
      // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
      const updatedNeed = await moveNeed(needId, 'CANCELED');
      setNeedData(updatedNeed);
      
      setToast({
        message: 'Need marked as CANCELED successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to cancel need:', error);
      setToast({
        message: 'Failed to cancel need: ' + error.message,
        type: 'error'
      });
    } finally {
      setStepLoading(prev => ({ ...prev, canceled: false }));
    }
  };

  const handleDeleteNeed = async () => {
    if (!window.confirm('Are you sure you want to delete this need? This action cannot be undone.')) {
      return;
    }

    try {
      setStepLoading(prev => ({ ...prev, delete: true }));
      
      // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
      const updatedNeed = await moveNeed(needId, 'DELETED');
      setNeedData(updatedNeed);
      
      setToast({
        message: 'Need deleted successfully!',
        type: 'success'
      });
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to delete need:', error);
      setToast({
        message: 'Failed to delete need: ' + error.message,
        type: 'error'
      });
    } finally {
      setStepLoading(prev => ({ ...prev, delete: false }));
    }
  };

  // Handle freeze need function - toggle
  const handleFreezeNeed = async () => {
    try {
      setStepLoading(prev => ({ ...prev, freeze: true }));

      const currentCol = (needData?.column || need?.column);
      const isFrozenNow = (currentCol === 'FROZEN') || ((needData?.status || need?.status) === 'frozen');

      if (isFrozenNow) {
        // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
        const targetColumn = prevColumnRef.current.frozen || 'NO_CANDIDATE';
        const updatedNeed = await moveNeed(needId, targetColumn);
        setNeedData(updatedNeed);
        prevColumnRef.current.frozen = null;
        setToast({ message: 'Need unfrozen successfully!', type: 'success' });
      } else {
        // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
        prevColumnRef.current.frozen = currentCol;
        const updatedNeed = await moveNeed(needId, 'FROZEN');
        setNeedData(updatedNeed);
        setToast({ message: 'Need frozen successfully! The need is now temporarily on hold.', type: 'success' });
      }
    } catch (error) {
      console.error('❄️ handleFreezeNeed error:', { needId, error: error.message, errorDetails: error.response?.data });
      setToast({ message: 'Failed to update freeze status: ' + error.message, type: 'error' });
    } finally {
      setStepLoading(prev => ({ ...prev, freeze: false }));
    }
  };

  // Handle push need function - using same logic as markNeedSold
  const handlePushNeed = async () => {
    try {
      setStepLoading(prev => ({ ...prev, push: true }));

      const currentCol = (needData?.column || need?.column);
      const isPushedNow = (currentCol === 'PUSH') || ((needData?.status || need?.status) === 'push');

      if (isPushedNow) {
        // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
        const targetColumn = prevColumnRef.current.push || 'NO_CANDIDATE';
        const updatedNeed = await moveNeed(needId, targetColumn);
        setNeedData(updatedNeed);
        prevColumnRef.current.push = null;
        setToast({ message: 'Push disabled successfully!', type: 'success' });
      } else {
        // ✅ FIX: Use moveNeed for column/status updates instead of updateNeed
        prevColumnRef.current.push = currentCol;
        const updatedNeed = await moveNeed(needId, 'PUSH');
        setNeedData(updatedNeed);
        setToast({ message: 'Need pushed successfully! Proactive CV sending activated.', type: 'success' });
      }
    } catch (error) {
      console.error('🚀 handlePushNeed error:', { needId, error: error.message, errorDetails: error.response?.data });
      setToast({ message: 'Failed to update push status: ' + error.message, type: 'error' });
    } finally {
      setStepLoading(prev => ({ ...prev, push: false }));
    }
  };

  // Get JD analysis status badge
  const getJDAnalysisStatusBadge = () => {
    if (!need.jd_analysis_status) return null;
    
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', text: 'JD Analysis Pending', icon: Clock },
      COMPLETED: { color: 'bg-green-100 text-green-800', text: 'JD Analysis Complete', icon: CheckCircle },
      ERROR: { color: 'bg-red-100 text-red-800', text: 'JD Analysis Failed', icon: XCircle }
    };

    const config = statusConfig[need.jd_analysis_status];
    if (!config) return null;

    const IconComponent = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <IconComponent className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  // ✅ FIX: Enhanced parseJsonField to handle different data types
  const parseJsonField = (field) => {
    if (!field) return [];
    
    // If it's already an array, return it
    if (Array.isArray(field)) return field;
    
    // If it's a string, try to parse it
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        // If parsing fails, split by comma as fallback
        return field.split(',').map(item => item.trim()).filter(item => item.length > 0);
      }
    }
    
    return [];
  };

  // ✅ FIX: Simplifier la fonction getJDAnalysisFromSchema
  const getJDAnalysisFromSchema = () => {
    // Helper function to get array data from multiple possible sources
    const getArrayField = (fieldName) => {
      // Try camelCase version first
      const camelCase = need[fieldName];
      if (camelCase && Array.isArray(camelCase) && camelCase.length > 0) {
        return camelCase;
      }
      
      // Try snake_case version
      const snakeCase = need[fieldName.replace(/([A-Z])/g, '_$1').toLowerCase()];
      if (snakeCase && Array.isArray(snakeCase) && snakeCase.length > 0) {
        return snakeCase;
      }
      
      // Try _json version
      const jsonField = need[`${fieldName.replace(/([A-Z])/g, '_$1').toLowerCase()}_json`];
      if (jsonField) {
        return parseJsonField(jsonField);
      }
      
      return [];
    };
    
    // Helper function to get string field from multiple possible sources
    const getStringField = (fieldName) => {
      // Try camelCase version first
      const camelCase = need[fieldName];
      if (camelCase) return camelCase;
      
      // Try snake_case version
      const snakeCase = need[fieldName.replace(/([A-Z])/g, '_$1').toLowerCase()];
      if (snakeCase) return snakeCase;
      
      return null;
    };
    
    return {
      title: need.title,
      seniority: getStringField('jdSeniority'),
      team: getStringField('jdTeam'),
      projectname: getStringField('jdProjectName'),
      location: getStringField('jdLocation'),
      education: getStringField('jdEducation'),
      experienceyears: getStringField('jdExperienceYears'),
      company: getStringField('jdCompany'),
      responsibilities: getArrayField('jdResponsibilities'),
      requiredskills: getArrayField('jdRequiredSkills'),
      desiredskills: getArrayField('jdDesiredSkills'),
      soft_skills: getArrayField('jdSoftSkills')
    };
  };

  // ✅ FIX: Update hasJDFields to check both possible field names
  const hasJDFields = () => {
    const analysis = getJDAnalysisFromSchema();
    return analysis.seniority || analysis.team || analysis.projectname || 
           analysis.location || analysis.education || analysis.experienceyears ||
           analysis.company || 
           (analysis.responsibilities && analysis.responsibilities.length > 0) ||
           (analysis.requiredskills && analysis.requiredskills.length > 0) ||
           (analysis.desiredskills && analysis.desiredskills.length > 0) ||
           (analysis.soft_skills && analysis.soft_skills.length > 0);
  };

  // Update the handleCreatePerfectMail function to just open the modal
  const handleCreatePerfectMail = () => {
    setShowEmailModal(true);
  };

  // ✅ FIX: Add the missing renderJDAnalysis function
  const renderJDAnalysis = () => {
    if (!need.jd_analysis_json && !hasJDFields()) return null;

    const analysis = getJDAnalysisFromSchema();
    
    return (
      <div className="mt-6 space-y-6">
        {/* Analysis Header with Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900">AI Job Analysis</h4>
              <p className="text-sm text-gray-600">Comprehensive position breakdown</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-green-700">Analysis Complete</span>
          </div>
        </div>

        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {analysis.seniority && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  SENIORITY
                </span>
              </div>
              <div className="font-bold text-gray-900 text-lg">{analysis.seniority}</div>
              <div className="text-xs text-blue-600 mt-1">Experience Level</div>
            </div>
          )}
          
          {analysis.education && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-white" />
                </div>
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  EDUCATION
                </span>
              </div>
              <div className="font-bold text-gray-900 text-lg">{analysis.education}</div>
              <div className="text-xs text-green-600 mt-1">Required Degree</div>
            </div>
          )}
          
          {analysis.experienceyears && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                  EXPERIENCE
                </span>
              </div>
              <div className="font-bold text-gray-900 text-lg">{analysis.experienceyears} years</div>
              <div className="text-xs text-purple-600 mt-1">Minimum Required</div>
            </div>
          )}
          
          {analysis.location && analysis.location !== 'Non spécifiée' && (
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                  <MapPinIcon className="w-4 h-4 text-white" />
                </div>
                <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                  LOCATION
                </span>
              </div>
              <div className="font-bold text-gray-900 text-lg">{analysis.location}</div>
              <div className="text-xs text-orange-600 mt-1">Work Location</div>
            </div>
          )}
        </div>

        {/* Position Context */}
        {(analysis.team || analysis.projectname || analysis.company) && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h5 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Position Context
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {analysis.company && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Building className="w-4 h-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company</span>
                  </div>
                  <div className="font-bold text-gray-900">{analysis.company}</div>
                </div>
              )}
              {analysis.team && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Team Size</span>
                  </div>
                  <div className="font-bold text-gray-900">{analysis.team}</div>
                </div>
              )}
              {analysis.projectname && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Project</span>
                  </div>
                  <div className="font-bold text-gray-900">{analysis.projectname}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Skills Matrix */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Required Skills */}
          {analysis.requiredskills && analysis.requiredskills.length > 0 && (
            <div className="bg-white border border-red-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-bold text-gray-900 flex items-center gap-2">
                  <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center">
                    <Star className="w-3 h-3 text-white" />
                  </div>
                  Required Skills
                </h5>
                <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                  {analysis.requiredskills.length} skills
                </span>
              </div>
              <div className="space-y-2">
                {analysis.requiredskills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="text-sm font-medium text-gray-900 flex-1">{skill}</span>
                    <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">CRITICAL</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Desired Skills */}
          {analysis.desiredskills && analysis.desiredskills.length > 0 && (
            <div className="bg-white border border-green-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-bold text-gray-900 flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                    <Award className="w-3 h-3 text-white" />
                  </div>
                  Desired Skills
                </h5>
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  {analysis.desiredskills.length} skills
                </span>
              </div>
              <div className="space-y-2">
                {analysis.desiredskills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm font-medium text-gray-900 flex-1">{skill}</span>
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">NICE TO HAVE</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Soft Skills */}
        {analysis.soft_skills && analysis.soft_skills.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h5 className="font-bold text-gray-900 flex items-center gap-2">
                <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center">
                  <User className="w-3 h-3 text-white" />
                </div>
                Soft Skills & Personality
              </h5>
              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                {analysis.soft_skills.length} traits
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {analysis.soft_skills.map((skill, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium text-gray-900">{skill}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Responsibilities */}
        {analysis.responsibilities && analysis.responsibilities.length > 0 && (
          <div className="bg-white border border-blue-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h5 className="font-bold text-gray-900 flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
                Key Responsibilities
              </h5>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                {analysis.responsibilities.length} tasks
              </span>
            </div>
            <div className="space-y-3">
              {analysis.responsibilities.map((resp, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-white">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{resp}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analysis Metadata */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">AI Analysis Status</div>
                <div className="text-xs text-gray-600">Generated from job description</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-blue-600">{need.jdAnalysisStatus || 'COMPLETED'}</div>
              <div className="text-xs text-gray-500">
                {need.updatedAt ? `Updated ${new Date(need.updatedAt).toLocaleDateString()}` : 'Recently updated'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ FIX: Add safe tooltip content rendering
  const renderTooltipContent = (content) => {
    if (!content) return '';
    
    // If it's an object, format it safely
    if (typeof content === 'object') {
      try {
        return JSON.stringify(content, null, 2);
      } catch (error) {
        return 'Content unavailable (formatting error)';
      }
    }
    
    // Return as string
    return String(content);
  };

  // ✅ NEW: Validation function for opportunity creation
  const validateOpportunityCreation = () => {
    const missingData = [];
    
    if (missingOrderClients) {
      missingData.push('Order Client contacts');
    }
    
    if (missingTjm) {
      missingData.push('TJM (daily rate)');
    }
    
    return missingData;
  };

  // ✅ NEW: Enhanced workflow trigger with validation
  const handleWorkflowTrigger = async (workflowType) => {
    if (workflowType === 'opportunity') {
      const missingData = validateOpportunityCreation();
      if (missingData.length > 0) {
        setToast({
          message: `Cannot create opportunity. Missing required data: ${missingData.join(', ')}. Please add this information first.`,
          type: 'error'
        });
        setShowEditNeedModal(true);
        return;
      }
    }
    
    try {
      // Create workflow request
      await createWorkflowRequest(workflowType);
      
      setToast({
        message: `${workflowType} workflow request created successfully! Redirecting to workflow management...`,
        type: 'success'
      });
      
      // Close the modal and redirect to workflow requests page
      setTimeout(() => {
        onClose();
        navigate('/admin/workflows');
      }, 1500);
      
    } catch (error) {
      console.error('Failed to create workflow request:', error);
      setToast({
        message: `Failed to create ${workflowType} workflow request: ${error.message}`,
        type: 'error'
      });
    }
  };

  // ✅ NEW: Create workflow request function
  const createWorkflowRequest = async (workflowType) => {
    const workflowTypeMap = {
      'opportunity': 'salesforce_opportunity_create',
      'mission': 'salesforce_mission_create'
    };

    const payload = {
      stepId: `manual_${workflowType}_${needId}`,
      type: workflowTypeMap[workflowType],
      payload: {
        needId,
        needData: {
          title: needData?.title || need?.title,
          client: needData?.client || need?.client,
          company: needData?.jdCompany || need?.jdCompany,
          projectName: needData?.jdProjectName || need?.jdProjectName,
          orderClients: needData?.orderClients || need?.orderClients,
          priority: needData?.priority || need?.priority || 'P2',
          description: needData?.description || need?.description,
          tjm: needData?.tjm || need?.tjm
        },
        triggeredBy: 'manual_ui',
        source: 'process_tracker',
        ...(workflowType === 'opportunity' && { 
          autoCreateProduct: true,
          autoCreateMission: true
        })
      }
    };

    return await workflowRequestsApi.createWorkflowRequest(payload);
  };

  // ✅ NEW: Enhanced download function with correct file extension
  const handleDownloadJD = async () => {
    try {
      const fileData = await uploadsApi.downloadJDFile(needId);
      
      // ✅ NEW: Create a custom filename
      const clientName = need.client || need.jdCompany || 'Unknown_Client';
      const needTitle = need.title || 'Unknown_Title';
      
      // Clean the names for filename compatibility
      const cleanClientName = clientName.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_');
      const cleanNeedTitle = needTitle.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_');
      
      // ✅ FIXED: Extract file extension from S3 URL or object key
      let fileExtension = '.pdf'; // Default fallback
      
      // Try to get extension from the jd_s3_url
      const jdUrl = need.jdS3Url || need.jd_s3_url;
      if (jdUrl) {
        // Extract extension from S3 URL/key
        const urlMatch = jdUrl.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
        if (urlMatch && urlMatch[1]) {
          fileExtension = '.' + urlMatch[1];
        }
      }
      
      const customFilename = `${cleanClientName}_${cleanNeedTitle}${fileExtension}`;
      
      // ✅ FIXED: Create blob and download directly
      const blob = new Blob([fileData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = customFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Failed to download JD:', error);
      setToast({
        message: 'Failed to download Job Description: ' + error.message,
        type: 'error'
      });
    }
  };

  // JD re-analysis with new file
  const handleJDReanalysis = async (file) => {
    if (!file) return;
    if (!needId) {
      setToast({ message: 'Need ID is missing. Please reload the page.', type: 'error' });
      return;
    }

    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      setToast({ message: 'Please upload a PDF, DOC, or DOCX file', type: 'error' });
      return;
    }

    try {
      setAnalyzingJD(true);

      const formData = new FormData();
      formData.append('files', file);
      formData.append('file_types', 'jd');
      formData.append('api_config_json', JSON.stringify({
        api_key: 'default',
        email: 'user@example.com',
        username: 'user',
        need_id: needId
      }));

      await api.post('/analyses-ia/file-analyses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (error) {
      console.error('JD re-analysis failed:', error);
      setToast({ message: 'JD re-analysis failed: ' + error.message, type: 'error' });
      setAnalyzingJD(false);
    }

    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  // JD re-analysis of existing file
  const handleJDReanalyzeExisting = async () => {
    if (!needId) {
      setToast({ message: 'Need ID is missing. Please reload the page.', type: 'error' });
      return;
    }

    try {
      setAnalyzingJD(true);
      await uploadsApi.reanalyzeJD(needId);
    } catch (error) {
      console.error('JD re-analysis failed:', error);
      setToast({ message: 'JD re-analysis failed: ' + error.message, type: 'error' });
      setAnalyzingJD(false);
    }
  };

  if (!isOpen) return null;

  const selectWorkspace = workspace => {
    setActiveWorkspace(workspace);
    if (workspace === 'need') setActiveTab('need');
    if (workspace === 'candidates' && activeTab === 'need') setActiveTab('identified');
  };

  const refreshIdentifiedCandidates = async () => {
    const loadedCandidates = await loadCandidatesWithStatusForNeed(needId);
    await fetchCandidates(loadedCandidates);
  };

  const jdAnalysis = getJDAnalysisFromSchema();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="process-tracker-title" className="bg-white rounded-lg shadow-xl w-full h-full sm:h-[90vh] sm:max-w-6xl flex flex-col">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b bg-white px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
              <h2 id="process-tracker-title" className="truncate text-lg font-semibold text-gray-900 sm:text-xl">
              {need.title}
              </h2>
              <Badge className="flex-shrink-0 bg-blue-100 text-blue-800">
                {needStatusLabel[need.status] || 'Actif'}
              </Badge>
            </div>
            <p className="mt-1 truncate text-xs text-gray-500">
              {[need.client || need.jdCompany, need.jdLocation, need.priority].filter(Boolean).join(' · ') || 'Suivi du besoin'}
            </p>
          </div>
          
          {/* Quick Actions in Header - Mobile Optimized */}
          <div className="flex items-center gap-2 sm:gap-3">
            {!isRecruiter && (
              <>
                <button
                  onClick={() => setShowEditNeedModal(true)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 hover:border-green-300 transition-all"
                  title="Edit Need Details"
                >
                  <Edit3 className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden xs:inline">Modifier</span>
                </button>
                
                <button
                  onClick={handleCreatePerfectMail}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-100 hover:border-purple-300 transition-all"
                  title="Create Perfect Mail with AI"
                >
                  <Sparkles className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden xs:inline">Mail IA</span>
                </button>
              </>
            )}
            
            <button
              onClick={onClose}
              aria-label="Fermer"
              className="min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-600 ml-1 sm:ml-2 flex items-center justify-center flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div role="tablist" aria-label="Espaces du besoin" className="flex min-h-[48px] shrink-0 items-center gap-3 border-b px-3 sm:gap-6 sm:px-6">
          {[
            ['need', 'Détails du besoin'],
            ['candidates', `Candidats (${candidates.length + (canManageJobConnect ? receivedApplicationsCount : 0)})`],
            ...(canManageJobConnect ? [['diffusion', 'Diffusion']] : []),
          ].map(([workspace, label]) => (
            <button key={workspace} role="tab" aria-selected={activeWorkspace === workspace} onClick={() => selectWorkspace(workspace)} className={`min-h-[48px] border-b-2 px-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 ${activeWorkspace === workspace ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
          ))}
        </div>

        {activeWorkspace === 'candidates' && (
          <div className="shrink-0 overflow-x-auto border-b bg-gray-50" aria-label="Navigation candidats">
            <div role="tablist" aria-label="Navigation candidats" className="flex min-w-max items-center gap-1 px-3 sm:px-6">
              {[
                ['identified', `Identifiés (${identifiedCandidates.length})`],
                ['sent', `Envoyés (${sentCandidates.length})`],
                ['inprogress', `Entretien / GO-NOGO (${inProgressCandidates.length})`],
                ['rejected', `Rejetés (${rejectedCandidates.length})`],
                ...(canManageJobConnect ? [['received', `Candidatures reçues (${receivedApplicationsCount})`]] : []),
              ].map(([tab, label]) => (
                <button key={tab} role="tab" aria-selected={activeTab === tab} onClick={() => setActiveTab(tab)} className={`min-h-[44px] border-b-2 px-3 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 ${activeTab === tab ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {activeWorkspace === 'need' && activeTab === 'need' && (
            <div className="mx-auto max-w-6xl space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Vue d'ensemble</h3>
                  <p className="text-sm text-gray-500">Les informations utiles et les actions du besoin au même endroit.</p>
                </div>
                <Badge className="bg-green-100 text-green-800">Données à jour</Badge>
              </div>

              <div className="grid overflow-hidden rounded-xl border border-gray-200 bg-white sm:grid-cols-2 lg:grid-cols-6">
                {[
                  ['Client', need.client || need.jdCompany || 'Non renseigné'],
                  ['Priorité', need.priority || 'Non définie'],
                  ['Localisation', jdAnalysis.location || 'Non renseignée'],
                  ['Télétravail', need.remote || need.telework || 'Non renseigné'],
                  ['TJM', need.tjm ? `${need.tjm} €` : 'Non renseigné'],
                  ['Candidats', `${candidates.length} profil${candidates.length > 1 ? 's' : ''}`],
                ].map(([label, value]) => (
                  <div key={label} className="border-b border-gray-200 p-3 last:border-0 sm:border-r lg:border-b-0">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
                    <strong className="mt-1 block truncate text-sm text-gray-900">{value}</strong>
                  </div>
                ))}
              </div>

              <div className="grid gap-3 lg:grid-cols-[1fr_280px]">
                <section className="rounded-xl border border-gray-200 bg-white p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Résumé et analyse utile</h4>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    {jdAnalysis.projectname || jdAnalysis.team || need.description || 'Aucun résumé détaillé n’est encore disponible pour ce besoin.'}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {jdAnalysis.requiredskills.slice(0, 6).map(skill => (
                      <span key={skill} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">{skill}</span>
                    ))}
                  </div>
                </section>
                <section className="rounded-xl border border-gray-200 bg-white p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Prochaine action</h4>
                  <p className="mt-2 text-sm font-medium text-gray-800">
                    {candidates.length > 0 ? 'Poursuivre le suivi des candidats' : 'Ajouter le premier candidat'}
                  </p>
                  <button
                    onClick={() => selectWorkspace('candidates')}
                    className="mt-3 min-h-[44px] rounded-md bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {candidates.length > 0 ? 'Voir les candidats' : 'Ajouter un candidat'}
                  </button>
                </section>
              </div>

              {!isRecruiter && (
                <section className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Actions du besoin</h4>
                    <p className="text-xs text-gray-500">Ces actions modifient le statut global du besoin.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={handleMarkAsSold} disabled={stepLoading.sale || need.status === 'sold'} className="min-h-[44px] rounded-md border border-green-200 px-3 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50">
                      {stepLoading.sale ? 'Mise à jour...' : 'Marquer gagné'}
                    </button>
                    <button onClick={handleFreezeNeed} disabled={stepLoading.freeze} className="min-h-[44px] rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      {(need.column === 'FROZEN' || need.status === 'frozen') ? 'Réactiver' : 'Mettre en pause'}
                    </button>
                    <button onClick={handlePushNeed} disabled={stepLoading.push} className="min-h-[44px] rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      {(need.column === 'PUSH' || need.status === 'push') ? 'Désactiver le push' : 'Activer le push'}
                    </button>
                    <button onClick={handleMarkAsCanceled} disabled={stepLoading.canceled || need.status === 'canceled'} className="min-h-[44px] rounded-md border border-amber-200 px-3 text-sm text-amber-800 hover:bg-amber-50 disabled:opacity-50">Annuler</button>
                    <button onClick={handleDeleteNeed} disabled={stepLoading.delete} className="min-h-[44px] rounded-md border border-red-200 px-3 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">Clôturer</button>
                  </div>
                </section>
              )}

              <details className="group rounded-xl border border-gray-200 bg-white" open={!need.orderClients || need.orderClients.length === 0}>
                <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-3 px-4 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <span>Clients donneurs d'ordre</span>
                  <span className="text-xs font-normal text-gray-500">
                    {need.orderClients?.length ? `${need.orderClients.length} contact${need.orderClients.length > 1 ? 's' : ''}` : 'Informations manquantes'} · ouvrir
                  </span>
                </summary>
                <div className="border-t border-gray-100 p-4">
                  {need.orderClients?.length ? (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {need.orderClients.map((orderClient, index) => (
                        <div key={orderClient} className="rounded-lg bg-gray-50 p-3">
                          <strong className="block text-sm text-gray-900">{orderClient}</strong>
                          <span className="text-xs text-gray-500">{need.orderClientsEmail?.[index] || 'Email non renseigné'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-amber-800">Ajoutez les interlocuteurs client pour faciliter les relances et les envois.</p>
                      {!isRecruiter && <button onClick={() => setShowEditNeedModal(true)} className="min-h-[44px] rounded-md bg-blue-600 px-4 text-sm font-medium text-white">Ajouter les contacts</button>}
                    </div>
                  )}
                </div>
              </details>

              <details className="group rounded-xl border border-gray-200 bg-white">
                <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-3 px-4 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <span>Fiche et analyse du besoin</span>
                  <span className="flex items-center gap-2 text-xs font-normal text-gray-500">{getJDAnalysisStatusBadge()} ouvrir</span>
                </summary>
                <div className="space-y-4 border-t border-gray-100 p-4">
                  {(need.jdS3Url && need.jdS3Url !== 'None' && need.jdS3Url !== 'null') || (need.jobDescriptionFile && need.jobDescriptionFile !== 'None' && need.jobDescriptionFile !== 'null') ? (
                    <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <div><strong className="block text-sm">Fiche de poste disponible</strong><span className="text-xs text-gray-500">Document importé et analysé</span></div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={handleDownloadJD} className="min-h-[44px] rounded-md border border-gray-300 px-3 text-sm text-gray-700"><Download className="mr-1 inline h-4 w-4" />Télécharger</button>
                        {!isRecruiter && (
                          <>
                            <input type="file" ref={jdFileInputRef} accept=".pdf,.doc,.docx" className="hidden" onChange={event => event.target.files?.[0] && handleJDReanalysis(event.target.files[0])} />
                            <button onClick={() => jdFileInputRef.current?.click()} disabled={analyzingJD} className="min-h-[44px] rounded-md border border-blue-200 px-3 text-sm text-blue-700 disabled:opacity-50"><Upload className="mr-1 inline h-4 w-4" />{analyzingJD ? 'Analyse...' : 'Réanalyser'}</button>
                            <button onClick={handleJDReanalyzeExisting} disabled={analyzingJD} className="min-h-[44px] rounded-md border border-blue-200 px-3 text-sm text-blue-700 disabled:opacity-50"><RefreshCw className="mr-1 inline h-4 w-4" />Relancer l'analyse</button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <FileUploadButton file={need.jobDescriptionFile} presignFn={filename => uploadsApi.presignJD(needId, filename)} completeFn={body => uploadsApi.completeJD(needId, body)} downloadFn={() => uploadsApi.downloadJD(needId)} deleteFn={() => uploadsApi.deleteJD(needId)} onUpload={handleJDUpload} onDelete={handleJDDelete} allowedTypes={['.pdf', '.doc', '.docx']} uploadText="Importer une fiche de poste" className="w-full" />
                  )}
                  {renderJDAnalysis()}
                </div>
              </details>

              <details className="group rounded-xl border border-gray-200 bg-white">
                <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-3 px-4 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <span>Salesforce</span>
                  <span className="text-xs font-normal text-gray-500">Opportunité {need.opportunitySalesforceUrl ? 'liée' : 'à créer'} · mission {need.missionSalesforceUrl ? 'liée' : 'à créer'} · ouvrir</span>
                </summary>
                <div className="space-y-3 border-t border-gray-100 p-4">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="flex min-h-[48px] items-center justify-between rounded-lg bg-gray-50 px-3 text-sm"><span>Opportunité Salesforce</span>{need.opportunitySalesforceUrl ? <a href={need.opportunitySalesforceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700">Voir</a> : <span className="text-gray-500">Non créée</span>}</div>
                    <div className="flex min-h-[48px] items-center justify-between rounded-lg bg-gray-50 px-3 text-sm"><span>Mission Salesforce</span>{need.missionSalesforceUrl ? <a href={need.missionSalesforceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700">Voir</a> : <span className="text-gray-500">Non créée</span>}</div>
                  </div>
                  {showRpaActions && !isRecruiter && (
                    <div className="flex flex-wrap gap-2">
                      {missingOpportunity && <button onClick={() => handleWorkflowTrigger('opportunity')} disabled={isLoading('opportunity')} className="min-h-[44px] rounded-md bg-blue-600 px-4 text-sm text-white disabled:opacity-50">Créer l'opportunité</button>}
                      {missingMission && <button onClick={() => handleWorkflowTrigger('mission')} disabled={isLoading('mission')} className="min-h-[44px] rounded-md bg-blue-600 px-4 text-sm text-white disabled:opacity-50">Créer la mission</button>}
                      {missingOpportunity && missingMission && <button onClick={() => triggerBatchWorkflow(['opportunity', 'mission'])} disabled={isLoading('opportunity') || isLoading('mission')} className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm text-gray-700 disabled:opacity-50">Créer les deux</button>}
                    </div>
                  )}
                </div>
              </details>
            </div>
          )}

          {canManageJobConnect && activeWorkspace === 'diffusion' && (
            <JobConnectDiffusionPanel
              need={need}
              canMutate={canManageJobConnect && !terminalNeed}
              applicationCount={receivedApplicationsCount}
              onOpenApplications={() => {
                setActiveWorkspace('candidates');
                setActiveTab('received');
              }}
            />
          )}

          {canManageJobConnect && activeWorkspace === 'candidates' && activeTab === 'received' && (
            <JobConnectApplicationsInbox
              needId={needId}
              needStatus={need.column || need.status}
              canMutate={canManageJobConnect && !terminalNeed}
              onIdentifiedRefresh={refreshIdentifiedCandidates}
              onCountChange={setReceivedApplicationsCount}
            />
          )}

          {activeWorkspace === 'candidates' && activeTab === 'identified' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Candidats identifiés ({identifiedCandidates.length})
                </h3>
                {!isRecruiter && (
                  <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Ajouter un candidat</span>
                  </button>
                )}
              </div>

              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement des candidats...</p>
                </div>
              )}

              {error && (
                <div className="text-center py-8">
                  <p className="text-red-600">{error}</p>
                  <button
                    onClick={fetchCandidates}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && identifiedCandidates.length === 0 && (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun candidat identifié</p>
                  <p className="text-sm text-gray-500">Ajoutez un candidat pour démarrer le suivi.</p>
                </div>
              )}

              {!loading && !error && identifiedCandidates.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {identifiedCandidates.map((candidate) => (
                    <CandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      needId={needId}
                      onUpdate={handleCandidateUpdate}
                      onAnalyzeCV={handleAnalyzeCV}
                      onRateCandidate={handleRateCandidate}
                      onShowTooltip={showTooltip}
                      onHideTooltip={hideTooltip}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sent Candidates */}
          {activeWorkspace === 'candidates' && activeTab === 'sent' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Candidats envoyés ({sentCandidates.length})
              </h3>

              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement des candidats...</p>
                </div>
              )}

              {!loading && !error && sentCandidates.length === 0 && (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun candidat envoyé</p>
                </div>
              )}

              {!loading && !error && sentCandidates.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sentCandidates.map((candidate) => (
                    <CandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      needId={needId}
                      onUpdate={handleCandidateUpdate}
                      onAnalyzeCV={handleAnalyzeCV}
                      onRateCandidate={handleRateCandidate}
                      onShowTooltip={showTooltip}
                      onHideTooltip={hideTooltip}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Interview / GO-NOGO */}
          {activeWorkspace === 'candidates' && activeTab === 'inprogress' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Entretiens / GO-NOGO ({inProgressCandidates.length})
              </h3>

              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement des candidats...</p>
                </div>
              )}

              {!loading && !error && inProgressCandidates.length === 0 && (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun candidat en entretien ou en décision</p>
                </div>
              )}

              {!loading && !error && inProgressCandidates.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {inProgressCandidates.map((candidate) => (
                    <CandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      needId={needId}
                      onUpdate={handleCandidateUpdate}
                      onAnalyzeCV={handleAnalyzeCV}
                      onRateCandidate={handleRateCandidate}
                      onShowTooltip={showTooltip}
                      onHideTooltip={hideTooltip}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeWorkspace === 'candidates' && activeTab === 'rejected' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <XCircle className="w-5 h-5 text-red-600 mr-2" />
                  Candidats rejetés ({rejectedCandidates.length})
                </h3>
                <div className="text-sm text-gray-500">
                  Sélectionnez un candidat pour consulter son détail
                </div>
              </div>

              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement des candidats...</p>
                </div>
              )}

              {!loading && rejectedCandidates.length === 0 && (
                <div className="text-center py-8">
                  <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun candidat rejeté</p>
                  <p className="text-sm text-gray-500">Candidates with GO/NOGO decision "NO_GO" will appear here</p>
                </div>
              )}

              {!loading && rejectedCandidates.length > 0 && (
                <div className="space-y-3">
                  {rejectedCandidates.map((candidate) => (
                    <CompactCandidateCard key={candidate.id} candidate={candidate} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Global Tooltip */}
      {tooltip.show && (
        <div 
          className="fixed bg-gray-800 text-white text-xs rounded shadow-xl z-[10001] pointer-events-none border border-gray-700"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translateX(-50%)',
            maxWidth: '600px',
            minWidth: '450px'
          }}
        >
          <div className="px-3 py-2 leading-relaxed whitespace-pre-wrap break-words">
            {renderTooltipContent(tooltip.content)}
          </div>
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-800"
          ></div>
        </div>
      )}

      {/* Modals */}
      {modalOpen && !isRecruiter && (
        <AddCandidateModal
          needId={needId}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={(result, meta) => {
            setModalOpen(false);

            // Show warning toast if candidate already existed
            if (meta?.wasExisting) {
              const existingNeeds = result?.needs?.filter(n => n.needId !== needId) || [];
              const needNames = existingNeeds.map(n => n.title || n.client || n.needId).join(', ');
              const message = needNames
                ? `This candidate already existed and was linked to: ${needNames}. Their data has been updated and they have been linked to this need.`
                : 'This candidate already existed and has been linked to this need. Their data has been updated.';
              setToast({ message, type: 'warning' });
            }

            // ✅ NEW: Check if we need to open edit modal for missing email
            if (result && result.needsEmailEdit && result.candidateData) {
              console.log('🔧 Opening edit modal for candidate with missing email:', result.candidateData);

              // Small delay to ensure AddCandidateModal has closed
              setTimeout(() => {
                setCandidateToEdit(result.candidateData);
                setShowEditCandidateModal(true);
              }, 300);
            }
          }}
        />
      )}
      
      {/* ✅ NEW: Edit Candidate Modal for post-AI analysis */}
      {showEditCandidateModal && candidateToEdit && (
        <EditCandidateModal
          candidate={candidateToEdit}
          open={showEditCandidateModal}
          onClose={() => {
            setShowEditCandidateModal(false);
            setCandidateToEdit(null);
          }}
          onSuccess={(updatedCandidate) => {
            setShowEditCandidateModal(false);
            setCandidateToEdit(null);
            
            // Refresh candidates to show updated data
            fetchCandidates();
            
            setToast({
              message: 'Candidate information updated successfully!',
              type: 'success'
            });
          }}
        />
      )}

      {/* Edit Need Modal */}
      {showEditNeedModal && (
        <EditNeedModal
          need={need}
          open={showEditNeedModal}
          onClose={() => setShowEditNeedModal(false)}
          onSuccess={(updatedNeed) => {
            setShowEditNeedModal(false);
            setNeedData(prev => ({ ...prev, ...updatedNeed }));
            onNeedUpdate?.();
          }}
        />
      )}

      {/* Email Generation Modal */}
      {showEmailModal && (
        <EmailGenerationModal
          needId={needId}
          candidates={candidates}
          candidateDetails={candidateDetails}
          open={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSuccess={() => {
            setShowEmailModal(false);
            setToast({
              message: 'Email generated successfully!',
              type: 'success'
            });
          }}
          onError={(errorMessage) => {
            setToast({
              message: errorMessage,
              type: 'error'
            });
          }}
        />
      )}

      {/* ✅ ADD: Candidate Detail Modal - PHASE 3: Stable callbacks */}
      {showCandidateDetailModal && selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          needId={needId}
          open={showCandidateDetailModal}
          onClose={handleCloseCandidateModal}
          onEdit={handleEditCandidate}
          onDownloadCV={handleDownloadCandidateCV}
          onCVReanalyzed={() => {
            fetchCandidates();
            setToast({ message: 'CV re-analysis completed. Candidate data has been updated.', type: 'success' });
          }}
        />
      )}

      {/* Toast notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ProcessTracker;
